import * as React from 'react';
import type {ModuleUIRef} from 'src/types/automation-workflow';
import type {ModalProps} from '@spaced-out/ui-design-system/lib/components/Modal';

import {Panel} from '@spaced-out/ui-design-system/lib/components/Panel';

import css from './multi-pane-panel.css';
import {FullScreenModal} from '@spaced-out/ui-design-system/lib';
import ErrorBoundary from 'src/components/lib/error-boundary';
import {PanelLoader} from 'src/components/automation/common/panel-loader';
import {FullscreenLoader} from 'src/components/automation/common/fullscreen-loader';


export type GenericObject = {
  // the + here (not well doc'd by flow) makes all object properties covariant
  // meaning that the value of any string-keyed property is allowed to have a more
  // specific type than `mixed` (i.e. string, number fn, etc)
  // (ie. the likely case for all real instances of GenericObject)
  // learn more here https://flow.org/blog/2016/10/04/Property-Variance/
  readonly [key: string]: unknown;
};

type OnClose = () => unknown;

export type MultiPanePanelProps<Extras> = ModalProps & {
  children?: React.ReactNode | null | undefined;
  onClose: OnClose;
  fullScreenPanel?: boolean;
  extras?: Extras;
  moduleUIRef?: ModuleUIRef;
  errorElement?: React.ReactNode;
  isLoading?: boolean;
};

export const MultiPanePanel = <Extras extends GenericObject>({
  moduleUIRef,
  children,
  onClose,
  isOpen,
  fullScreenPanel = false,
  extras,
  errorElement,
  isLoading,
  ...props
}: MultiPanePanelProps<Extras>) => {
  const effectiveErrorElement = errorElement || <div>Something went wrong</div>;
  const childrenWithProps = children
    ? // @ts-expect-error - TS2769 - No overload matches this call.
      React.cloneElement(children, {
        moduleUIRef,
        closePanel: onClose,
        extras: extras ?? {},
      })
    : null;

  if (isLoading) {
    if (fullScreenPanel) {
      return <FullscreenLoader />;
    }
    return (
      <Panel
        anchor="right"
        size="large"
        tapOutsideToClose={true}
        isOpen={isLoading}
      >
        <PanelLoader />
      </Panel>
    );
  }

  if (fullScreenPanel) {
    return (
      <FullScreenModal
        hideBackdrop={false}
        // initialFocus={1}
        tapOutsideToClose={false}
        {...props}
        isOpen={isOpen}
      >
        <ErrorBoundary fallback={() => effectiveErrorElement}>
          {childrenWithProps}
        </ErrorBoundary>
      </FullScreenModal>
    );
  }
  return (
    <Panel
      anchor="right"
      // initialFocus={0}
      size="large"
      tapOutsideToClose={true}
      {...props}
      isOpen={isOpen}
      classNames={{backdrop: css.backdrop}}
    >
      <ErrorBoundary fallback={() => effectiveErrorElement}>
        {childrenWithProps}
      </ErrorBoundary>
    </Panel>
  );
};
